import ObjectArrayView from '../../generic/container/ObjectArrayView';
import ObjectsByName from '../../generic/container/ObjectsByName';
import PolyMap from '../model/PolyMap';
import PolyMesh from '../model/PolyMesh';
import polyMeshValidation from './polyMeshValidation';
import removeDuplicateNormals from './removeDuplicateNormals';

export default function subdivision(mesh, smoothByMaterialIds) {

  // based on theory given on pages 623-624 of "Real-Time Rendering"
  const subdividePolyMap = function (polyMap, newFaceRangeOffsets, faceEdgeCreases, faceValueCorners) {

    const edgeVertexAdjacency = polyMap.edgeVertexAdjacency;
    const valueAdjacency = polyMap.valueAdjacency;
    const faceEdgeAdjacency = polyMap.faceEdgeAdjacency;
    /*
    if(!faceEdgeAdjacency.checkValidity()){
      throw 'This polymap contains an invalid surface and cannot be subdivided. Edge Adjacency will fail.';
    }//*/

    const values = polyMap.values;
    const valueType = values.type;
    const numValues = values.length;
    const faceRangeOffsets = polyMap.faceRangeOffsets;
    const numFaces = faceRangeOffsets.length - 1;
    const faceValueIndices = polyMap.faceValueIndices;
    const numEdges = edgeVertexAdjacency.getNumEdges();
    const numNewFaces = faceValueIndices.length;

    const vertexCorners = new Uint8Array(polyMap.values.length);
    for (let i = 0; i < faceValueCorners.length; i++) {
      vertexCorners[faceValueIndices[i]] = faceValueCorners[i];
    }

    if (!newFaceRangeOffsets) {
      newFaceRangeOffsets = new Uint32Array(numNewFaces + 1);
      for (let f = 0; f < newFaceRangeOffsets.length; f++) {
        newFaceRangeOffsets[f] = f * 4;
      }
    }

    const numNewFaceValueIndices = numNewFaces * 4;
    const newFaceValueIndices = new Uint32Array(numNewFaceValueIndices);

    // pre allocate all new vertices
    //    updated vertices (V) + edge centroids (E) + face centroids (V)
    const edgeOffset = numValues;
    const faceOffset = edgeOffset + numEdges;
    const numNewValues = numValues + numEdges + numFaces;
    const newValues = new ObjectArrayView(valueType, numNewValues);

    const valueEdgeValence = new Uint8Array(numValues);
    const valueFaceValence = new Uint8Array(numValues);
    const edgeFaceValence = new Uint8Array(numEdges);
    const edgeUnique = new Uint8Array(numEdges);

    const tempFaceCentroid = new valueType();
    const tempValue = new valueType();
    const tempNextValue = new valueType();
    const tempEdgeCentroid = new valueType();
    const tempConnectedValue = new valueType();
    const tempConnectedFace = new valueType();

    let nf = 0;

    const edgeCreases = new Uint8Array(numEdges);
    const valueCreases = new Uint8Array(numValues);

    for (let f = 0; f < numFaces; f++) {

      const faceBegin = faceRangeOffsets[f];
      const faceUntil = faceRangeOffsets[f + 1];
      const numFaceValues = faceUntil - faceBegin;

      let vNext = faceValueIndices[faceBegin];
      let vNextNext = faceValueIndices[faceBegin + 1 % numFaceValues];

      let eNext = edgeVertexAdjacency.findEdgeId(vNext, vNextNext);

      // calculate face centroid
      tempFaceCentroid.multiplyScalar(0.0);

      for (let fv = 0; fv < numFaceValues; fv++) {

        const v = vNext;
        vNext = vNextNext;
        vNextNext = faceValueIndices[faceBegin + ((fv + 2) % numFaceValues)];

        const e = eNext;
        eNext = edgeVertexAdjacency.findEdgeId(vNext, vNextNext);

        edgeFaceValence[e]++;
        valueFaceValence[v]++;

        // is this the first time processing this edge
        if (edgeUnique[e] === 0) {
          edgeUnique[e] = 1;

          valueEdgeValence[v]++;
          valueEdgeValence[vNext]++;

          if (faceEdgeCreases[faceBegin + fv]) {
            edgeCreases[e] = 1;
            valueCreases[v]++;
            valueCreases[vNext]++;
          }
        }

        values.getAt(v, tempValue);
        tempFaceCentroid.add(tempValue);

        newFaceValueIndices[nf++] = edgeOffset + e;
        newFaceValueIndices[nf++] = vNext;
        newFaceValueIndices[nf++] = edgeOffset + eNext;
        newFaceValueIndices[nf++] = faceOffset + f;
      }

      tempFaceCentroid.multiplyScalar(1.0 / numFaceValues);
      newValues.setAt(faceOffset + f, tempFaceCentroid);
    }
    // create new edges
    for (let e = 0; e < numEdges; e++) {

      const v = edgeVertexAdjacency.edgeVertices[e * 2];
      const vNext = edgeVertexAdjacency.edgeVertices[e * 2 + 1];

      values.getAt(v, tempEdgeCentroid);
      values.getAt(vNext, tempValue);
      tempEdgeCentroid.add(tempValue);
      let numNeighbors = 2;

      const edgeFaceCount = faceEdgeAdjacency.edgeFaceCounts[e];

      if (!edgeCreases[e] && edgeFaceCount === 2) {

        //if the edge contains 2 corners do not average with face
        if (!(vertexCorners[v] && vertexCorners[vNext])) {

          for (let ef = 0; ef < edgeFaceCount; ef++) {
            const f = faceEdgeAdjacency.edgeFaceIndices[e * 2 + ef];
            newValues.getAt(faceOffset + f, tempValue);
            tempEdgeCentroid.add(tempValue);
            numNeighbors++;
          }
        }
      }

      tempEdgeCentroid.multiplyScalar(1.0 / numNeighbors);

      newValues.setAt(edgeOffset + e, tempEdgeCentroid);
    }

    const useBoundaryValues = true;

    // create new values
    for (let v = 0; v < numValues; v++) {

      values.getAt(v, tempNextValue);
      const boundaryValue = valueEdgeValence[v] !== valueFaceValence[v];

      if (valueEdgeValence[v] !== 0 && vertexCorners[v] < 1) {

        // sum of connected vertices.
        const valueBegin = valueAdjacency.valueEdgeOffsets[v];
        const valueUntil = valueAdjacency.valueEdgeOffsets[v + 1];
        const numValueEdges = valueUntil - valueBegin;

        tempConnectedValue.multiplyScalar(0.0);
        let numConnectedValues = 0;

        if ((useBoundaryValues && boundaryValue) || valueCreases[v]) {

          for (let ve = 0; ve < numValueEdges; ve++) {
            const e = valueAdjacency.valueEdgeIndices[valueBegin + ve];
            if (edgeCreases[e]) {
              let vOther = edgeVertexAdjacency.edgeVertices[e * 2];
              if (vOther === v) {
                vOther = edgeVertexAdjacency.edgeVertices[e * 2 + 1];
              }

              values.getAt(vOther, tempValue);
              tempConnectedValue.add(tempValue);
              numConnectedValues++;
            }

          }

          tempNextValue.multiplyScalar(0.75).add(tempConnectedValue.multiplyScalar(0.25 / numConnectedValues));
        } else {

          for (let ve = 0; ve < numValueEdges; ve++) {
            const e = valueAdjacency.valueEdgeIndices[valueBegin + ve];
            let vOther = edgeVertexAdjacency.edgeVertices[e * 2];
            if (vOther === v) {
              vOther = edgeVertexAdjacency.edgeVertices[e * 2 + 1];
            }

            values.getAt(vOther, tempValue);
            tempConnectedValue.add(tempValue);
            numConnectedValues++;
          }

          tempConnectedValue.multiplyScalar(1 / (numConnectedValues * numConnectedValues));

          const valueBeginF = valueAdjacency.valueFaceOffsets[v];
          const valueUntilF = valueAdjacency.valueFaceOffsets[v + 1];
          const numValueFacesF = valueUntilF - valueBeginF;

          tempConnectedFace.multiplyScalar(0.0);
          let numConnectedFaces = 0;

          for (let vf = 0; vf < numValueFacesF; vf++) {

            const f = valueAdjacency.valueFaceIndices[valueBeginF + vf];
            newValues.getAt(faceOffset + f, tempValue);
            tempConnectedFace.add(tempValue);
            numConnectedFaces++;
          }

          tempConnectedFace.multiplyScalar(1 / (numConnectedFaces * numConnectedFaces));

          tempNextValue.multiplyScalar((numConnectedFaces - 2) / numConnectedFaces);
          tempNextValue.add(tempConnectedValue);
          tempNextValue.add(tempConnectedFace);

        }
      }

      newValues.setAt(v, tempNextValue);
    }

    const pm = {
      faceRangeOffsets: newFaceRangeOffsets,
      faceValueIndices: newFaceValueIndices,
      values: newValues,
    };

    return new PolyMap(pm);
  };

  polyMeshValidation(mesh);
  const polyMesh = removeDuplicateNormals(mesh);

  const faceEdgeCreases = new Uint8Array(polyMesh.positions.faceValueIndices.length);

  if (polyMesh.normalMap) {
    getFaceEdgeCreases(polyMesh.normalMap, faceEdgeCreases);
  }

  if (polyMesh.edgeCreaseWeights) {
    const faceEdgeAdjacency = polyMesh.positions.faceEdgeAdjacency;
    for (let fe = 0; fe < faceEdgeAdjacency.faceEdgeIndices.length; fe++) {
      const edgeIndex = faceEdgeAdjacency.faceEdgeIndices[fe];
      faceEdgeCreases[fe] = faceEdgeCreases[fe] || ( (polyMesh.edgeCreaseWeights[edgeIndex] > 0) ? 1 : 0 );
    }
  }

  if (smoothByMaterialIds && polyMesh.materialIds) {
    const faceEdgeAdjacency = polyMesh.positions.faceEdgeAdjacency;

    for (let fe = 0; fe < faceEdgeAdjacency.faceEdgeIndices.length; fe++) {
      const edgeIndex = faceEdgeAdjacency.faceEdgeIndices[fe];
      if (faceEdgeAdjacency.getNumFacesForEdgeId(edgeIndex) === 2 &&
        polyMesh.materialIds[faceEdgeAdjacency.getFaceForEdgeId(edgeIndex, 0)] !== polyMesh.materialIds[faceEdgeAdjacency.getFaceForEdgeId(edgeIndex, 1)]) {

        faceEdgeCreases[fe] += 1;
      }
    }

  }

  const faceValueCorners = getValueCorners(polyMesh.positions, faceEdgeCreases);

  const results = new PolyMesh();
  results.positions = subdividePolyMap(polyMesh.positions, null, faceEdgeCreases, faceValueCorners);
  results.faceRangeOffsets = results.positions.faceRangeOffsets;

  if (polyMesh.normalMap) {
    results.normalMap = subdividePolyMap(polyMesh.normalMap, results.faceRangeOffsets, faceEdgeCreases, faceValueCorners);
  }

  if (polyMesh.uvMaps) {
    const uvMaps = new ObjectsByName();
 
    for (let i = 0; i < polyMesh.uvMaps.length; i++) {
      const name = polyMesh.uvMaps.namesByIndex[i];
      const uvEdgeCreases = new Uint8Array( polyMesh.uvMaps.byName[ name ].faceValueIndices.length );
      getFaceEdgeCreases( polyMesh.uvMaps.byName[ name ], uvEdgeCreases );

      for( let j = 0; j < faceEdgeCreases.length; j ++ ) {
        uvEdgeCreases[j] = uvEdgeCreases[j] || faceEdgeCreases[j];
      }

      const uvFaceValueCorners = getValueCorners( polyMesh.uvMaps.byName[ name ], uvEdgeCreases );
      for( let j = 0; j < uvFaceValueCorners.length; j ++ ) {
        uvFaceValueCorners[j] = uvFaceValueCorners[j] || faceValueCorners[j];
      }
      uvMaps.set(name, subdividePolyMap( polyMesh.uvMaps.byName[name], results.faceRangeOffsets, uvEdgeCreases, uvFaceValueCorners ) );
    }

    results.uvMaps = uvMaps;
  }

  results.topologyChanged();
  return results;
};

const getFaceEdgeCreases = function (polyMap, faceEdgeCreases) {
  const faceEdgeAdjacency = polyMap.faceEdgeAdjacency;

  for (let fe = 0; fe < faceEdgeAdjacency.faceEdgeIndices.length; fe++) {
    const edgeIndex = faceEdgeAdjacency.faceEdgeIndices[fe];
    faceEdgeCreases[fe] = (faceEdgeAdjacency.edgeFaceCounts[edgeIndex] !== 2) ? 1 : 0;
  }
};

const getValueCorners = function (polyMap, faceEdgeCreases) {
  const edgeVertexAdjacency = polyMap.edgeVertexAdjacency;

  //converted to track indices that are corners instead of values
  const faceValueCorners = new Uint8Array(polyMap.faceValueIndices.length);
  const valueCreaseEdgeCount = new Uint8Array(polyMap.values.length);
  const edgeProcessed = new Uint8Array(edgeVertexAdjacency.getNumEdges());

  for (let f = 0; f < polyMap.faceRangeOffsets.length - 1; f++) {

    const faceBegin = polyMap.faceRangeOffsets[f];
    const faceUntil = polyMap.faceRangeOffsets[f + 1];
    const numFaceValues = faceUntil - faceBegin;

    let vNext = polyMap.faceValueIndices[faceBegin];

    for (let fv = 0; fv < numFaceValues; fv++) {
      const v = vNext;
      vNext = polyMap.faceValueIndices[faceBegin + (fv + 1) % numFaceValues];

      const e = edgeVertexAdjacency.findEdgeId(v, vNext);
      if (edgeProcessed[e] === 0) {
        edgeProcessed[e] = 1;

        if (faceEdgeCreases[faceBegin + fv]) {
          valueCreaseEdgeCount[v]++;
          valueCreaseEdgeCount[vNext]++;
        }
      }
    }
  }

  for (let i = 0; i < faceValueCorners.length; i++) {
    const valueIndex = polyMap.faceValueIndices[i];
    if (valueCreaseEdgeCount[valueIndex] > 2) {
      faceValueCorners[i] = 1;
    }
  }

  return faceValueCorners;

};



// WEBPACK FOOTER //
// ./src/polyMesh/geometric/operator/subdivision.js